import React, { useContext, useEffect, useState } from 'react';
import { style } from 'typestyle';
import Modal from 'react-modal';
import { useTranslation } from 'react-i18next';
import * as styles from './modal.styles';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import Column from '../../../core/components/column/column.component';
import { MainAxisAlignment } from '../../../core/components/flex/flex.component';
import Row from '../../../core/components/row/row.component';
import ActivityIndicator from '../../../core/components/activity-indicator/activity-indicator.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import StyleConstants from '../../../core/styles.constants';
import {
  cancelPermittedUserAccessEnd,
  getPermittedUserDeletionImpact,
  setPermittedUserAccessEnd,
  type PermittedUserDeletionImpact,
} from '../services/users.services';
import { accessEndProblem, dayAfter, formatOfficeDate, isInsideBookingWindow, needsAcknowledgement } from '../utils/access-end';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';

interface Props {
  readonly showModal: boolean;
  /** `OfficeUser.Id` — how both the impact and the schedule are addressed. */
  readonly officeUserId?: string;
  readonly email: string;
  /** The date already scheduled, when there is one, so the dialog opens on it. */
  readonly currentAccessEndsOn?: string;
  readonly handleClose: () => void;
  /** Called with the stored date, or undefined once a schedule has been called off. */
  readonly handleScheduled: (accessEndsOn?: string) => void;
}

const IMPACT_DEBOUNCE_MS = 300;

/** The inverse of `dayAfter`, for showing a stored date back as the last working day it came from. */
const previousDay = (date: string): string => {
  const [year, month, day] = date.split('-').map(Number);

  return new Date(Date.UTC(year, month - 1, day - 1)).toISOString().slice(0, 10);
};

/**
 * Schedules a person's removal for a date, and calls it off again.
 *
 * <p>
 * The delayed twin of the delete dialog, and deliberately the same shape: it says what the removal
 * costs before it happens and asks for that to be acknowledged. The difference is which day it
 * counts from. A leaver working until the 30th has three weeks of bookings that are not in
 * question, so counting from today would ask an administrator to acknowledge cancelling days that
 * will simply happen — and an acknowledgement that is routinely untrue stops being read.
 * </p>
 *
 * <p>
 * The field holds a <b>last working day</b>, because that is what HR says and what the person in
 * front of the administrator said. The API holds the first day <i>without</i> access, which is the
 * day after. The conversion is done here rather than asked of the user, and the resulting date is
 * spelled out so nobody has to trust it silently.
 * </p>
 */
const SchedulePermittedUserRemovalModal = ({
  showModal,
  officeUserId,
  email,
  currentAccessEndsOn,
  handleClose,
  handleScheduled,
}: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t, i18n } = useTranslation();
  const keycloak = useKeycloak();

  const [lastWorkingDay, setLastWorkingDay] = useState('');
  const [impact, setImpact] = useState<PermittedUserDeletionImpact | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  /** Which date the acknowledgement belongs to, so moving the date asks again. */
  const [acknowledgedFor, setAcknowledgedFor] = useState<string | undefined>(undefined);

  const accessEndsOn = lastWorkingDay === '' ? '' : dayAfter(lastWorkingDay);

  useEffect(() => {
    if (!showModal) {
      return;
    }

    // Opens on the date already scheduled, shown as the last working day it was derived from — the
    // day before. Anything else would make reopening the dialog look like a request to move the
    // date one day later every time.
    setLastWorkingDay(currentAccessEndsOn ? previousDay(currentAccessEndsOn) : '');
    setAcknowledgedFor(undefined);
    setError(undefined);
    setFailed(false);
    // Cleared, not merely refetched. Until the first answer lands, the previous colleague's counts
    // would otherwise back this dialog's date rules — the spinner and the disabled button hide it
    // today, which makes it a coincidence rather than a guarantee.
    setImpact(undefined);
  }, [showModal, currentAccessEndsOn]);

  useEffect(() => {
    // Guards against a late answer landing on a closed dialog, or on one reopened for a different
    // person — which would show the previous colleague's numbers under this one's name.
    let current = true;

    if (!showModal || officeUserId == null) {
      // Reset, so a dialog closed mid-fetch does not reopen showing a spinner that nothing is
      // going to clear.
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setFailed(false);

    // Debounced: the date field fires on every keystroke in a typed date, and each change is a
    // round trip that counts somebody's bookings.
    const timeout = setTimeout(() => {
      getPermittedUserDeletionImpact(officeUserId, keycloak, accessEndsOn === '' ? undefined : accessEndsOn)
        .then((result) => {
          if (!current) return;
          setImpact(result);
          // A failed count is reported as a failure, never substituted with zero: "nothing will be
          // cancelled" and "we could not find out" are different sentences, and only one of them
          // makes it safe to schedule.
          setFailed(result === undefined);
        })
        .catch(() => {
          if (current) setFailed(true);
        })
        .finally(() => {
          if (current) setLoading(false);
        });
    }, IMPACT_DEBOUNCE_MS);

    return (): void => {
      current = false;
      clearTimeout(timeout);
    };
  }, [showModal, officeUserId, accessEndsOn, keycloak]);

  const bounds = impact
    ? { today: impact.today, earliestOutsideBookingWindow: impact.earliestOutsideBookingWindow }
    : undefined;

  const problem = bounds ? accessEndProblem(accessEndsOn, bounds) : 'empty';
  const insideWindow = bounds ? isInsideBookingWindow(accessEndsOn, bounds) : false;
  const outstanding = needsAcknowledgement(impact?.hasImpact ?? false, acknowledgedFor, accessEndsOn);
  const confirmable = !loading && !saving && !failed && problem === undefined && !outstanding;

  const submit = async (): Promise<void> => {
    if (officeUserId == null || !confirmable) return;

    setSaving(true);
    setError(undefined);

    // The override is sent whenever the date is inside the window, because for a leaver that is
    // the ordinary case and the dialog has already said what it means. Sending it blind would be
    // wrong; sending it having shown the warning and the numbers is the whole consent.
    const outcome = await setPermittedUserAccessEnd(officeUserId, accessEndsOn, keycloak, true, insideWindow);

    setSaving(false);

    if (outcome.error) {
      setError(outcome.error);
      return;
    }

    handleScheduled(outcome.scheduled?.accessEndsOn ?? accessEndsOn);
  };

  const callOff = async (): Promise<void> => {
    if (officeUserId == null) return;

    setSaving(true);
    setError(undefined);

    const outcome = await cancelPermittedUserAccessEnd(officeUserId, keycloak);

    setSaving(false);

    if (outcome.error) {
      setError(outcome.error);
      return;
    }

    handleScheduled(undefined);
  };

  const renderImpact = (): React.JSX.Element | false => {
    if (loading) {
      return <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />;
    }

    if (failed || impact === undefined) {
      return <span className={style(styles.error)}>{t('delete-permitted-user-impact-unavailable')}</span>;
    }

    if (accessEndsOn === '') {
      return false;
    }

    if (!impact.hasImpact) {
      return (
        <span className={style(styles.bodyText)}>
          {t('schedule-removal-no-impact', { date: formatOfficeDate(accessEndsOn, i18n.language) })}
        </span>
      );
    }

    return (
      <Column>
        <span className={style(styles.bodyText)}>
          {t('schedule-removal-impact-intro', { date: formatOfficeDate(accessEndsOn, i18n.language) })}
        </span>
        {impact.bookings > 0 && (
          <span className={style(styles.bodyText)}>
            {t('delete-permitted-user-impact-bookings', { bookings: impact.bookings, days: impact.days })}
          </span>
        )}
        {impact.groupReservations > 0 && (
          <span className={style(styles.bodyText)}>
            {t('delete-permitted-user-impact-groups', { groups: impact.groupReservations })}
          </span>
        )}
        {impact.facilityReservations > 0 && (
          <span className={style(styles.bodyText)}>
            {t('delete-permitted-user-impact-facilities', { facilities: impact.facilityReservations })}
          </span>
        )}
        <span className={style(styles.bodyText)}>{t('delete-permitted-user-impact-history')}</span>
        <label className={style(styles.bodyText)} htmlFor="schedule-removal-acknowledge">
          <input
            id="schedule-removal-acknowledge"
            type="checkbox"
            checked={!outstanding}
            onChange={(event): void => {
              setAcknowledgedFor(event.target.checked ? accessEndsOn : undefined);
            }}
          />
          {t('schedule-removal-acknowledge')}
        </label>
      </Column>
    );
  };

  return (
    <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleClose} style={styles.modalStyle(isMobile)}>
      <Column>
        <span className={style(styles.bodyText)}>{t('schedule-removal-intro', { email })}</span>

        <label className={style(styles.bodyText)} htmlFor="schedule-removal-last-working-day">
          {t('schedule-removal-last-working-day')}
          <input
            id="schedule-removal-last-working-day"
            type="date"
            value={lastWorkingDay}
            onChange={(event): void => {
              setLastWorkingDay(event.target.value);
              setError(undefined);
            }}
          />
        </label>

        {/* Spelled out rather than implied. The field is a last working day and the column is the
            first day without access; stating both is what stops the off-by-one being discovered by
            the person who lost a day of access. */}
        {accessEndsOn !== '' && problem === undefined && (
          <span className={style(styles.bodyText)}>
            {t('schedule-removal-access-ends-on', { date: formatOfficeDate(accessEndsOn, i18n.language) })}
          </span>
        )}

        {problem === 'in-the-past' && (
          <span className={style(styles.error)}>{t('schedule-removal-date-in-the-past')}</span>
        )}

        {insideWindow && problem === undefined && (
          <span className={style(styles.bodyText)}>
            {t('schedule-removal-inside-booking-window', { days: impact?.planPeriodDays ?? 0 })}
          </span>
        )}

        {renderImpact()}

        {error != null && <span className={style(styles.error)}>{error}</span>}

        <Row itemSpacing={12} mainAxisAlignment={MainAxisAlignment.Stretch}>
          <div className={style(styles.button)}>
            <PrimaryButton
              fullWidth
              disabled={!confirmable}
              onClick={(): void => {
                submit().catch(() => undefined);
              }}
            >
              {t('schedule-removal-confirm')}
            </PrimaryButton>
          </div>
          {currentAccessEndsOn != null && (
            <div className={style(styles.button)}>
              <PrimaryButton
                fullWidth
                disabled={saving}
                onClick={(): void => {
                  callOff().catch(() => undefined);
                }}
              >
                {t('schedule-removal-call-off')}
              </PrimaryButton>
            </div>
          )}
          <div className={style(styles.button)}>
            <PrimaryButton fullWidth onClick={handleClose}>
              {t('cancel')}
            </PrimaryButton>
          </div>
        </Row>
      </Column>
    </Modal>
  );
};

export default SchedulePermittedUserRemovalModal;
